import React, { useState, useEffect, useCallback, useRef } from 'react';
import { startLegacyImport, getImportProgress, LegacyImportProgress } from '../api/legacy';
import './LegacyImportModal.css';

interface LegacyImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: File[];
  onImportComplete?: () => void;
}

interface ImportResult {
  filename: string;
  status: 'success' | 'warning' | 'error';
  claimantsImported: number;
  claimsImported: number;
  documentsImported: number;
  errors: string[];
  warnings: string[];
}

const LegacyImportModal: React.FC<LegacyImportModalProps> = ({
  isOpen,
  onClose,
  files,
  onImportComplete
}) => {
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<LegacyImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCalledOnComplete = useRef(false);
  const isPollingRef = useRef(false);
  const currentJobIdRef = useRef<string | null>(null);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  const isVisibleRef = useRef(true);

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      console.log(`[LegacyImportModal-${instanceId.current}] Visibility changed: ${isVisibleRef.current}`);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Clear state when modal opens/closes
  useEffect(() => {
    console.log(`[LegacyImportModal-${instanceId.current}] Modal state changed - isOpen: ${isOpen}`);
    
    if (!isOpen) {
      // Clear any existing interval
      if (pollIntervalRef.current) {
        console.log(`[LegacyImportModal-${instanceId.current}] Clearing interval on modal close`);
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      isPollingRef.current = false;
      currentJobIdRef.current = null;
      
      // Reset all state
      setIsImporting(false);
      setProgress(null);
      setError(null);
      setImportResults([]);
      setShowErrors(false);
      setShowWarnings(false);
      hasCalledOnComplete.current = false;
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    const currentInstanceId = instanceId.current;
    return () => {
      console.log(`[LegacyImportModal-${currentInstanceId}] Component unmounting - cleaning up`);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      isPollingRef.current = false;
      currentJobIdRef.current = null;
    };
  }, []);

  // Poll for progress updates
  const pollProgress = useCallback(async (jobId: string) => {
    // Validate job ID matches current job
    if (jobId !== currentJobIdRef.current) {
      console.log(`[LegacyImportModal-${instanceId.current}] Skipping poll - job ID mismatch. Expected: ${currentJobIdRef.current}, Got: ${jobId}`);
      return;
    }

    // Skip if not polling or not visible
    if (!isPollingRef.current || !isVisibleRef.current) {
      console.log(`[LegacyImportModal-${instanceId.current}] Skipping poll - polling: ${isPollingRef.current}, visible: ${isVisibleRef.current}`);
      return;
    }

    try {
      const progressData = await getImportProgress(jobId);
      
      // Still polling? Update progress
      if (isPollingRef.current) {
        setProgress(progressData);
      }

      // Check if import is complete
      if (progressData.status === 'completed' || progressData.status === 'failed') {
        console.log(`[LegacyImportModal-${instanceId.current}] Import ${progressData.status} for job ${jobId}`);
        
        // Stop polling
        isPollingRef.current = false;
        currentJobIdRef.current = null;
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        // Process results
        if (progressData.status === 'completed') {
          // Create results for all imported files
          const results: ImportResult[] = files.map((file, index) => ({
            filename: file.name,
            status: progressData.errorCount > 0 ? 'warning' : 'success',
            claimantsImported: Math.floor(progressData.successCount / files.length), // Distribute counts
            claimsImported: 0, // This would need to come from backend
            documentsImported: 0, // This would need to come from backend
            errors: index === 0 ? (progressData.errors || []) : [], // Show errors on first file for now
            warnings: [] // This would need to come from backend
          }));
          setImportResults(results);
          
          // Call onImportComplete only once when truly completed
          if (onImportComplete && !hasCalledOnComplete.current) {
            hasCalledOnComplete.current = true;
            setTimeout(() => {
              onImportComplete();
            }, 0);
          }
        } else if (progressData.status === 'failed') {
          setError('Import failed. Please check the errors below.');
        }

        setIsImporting(false);
      }
    } catch (err: any) {
      console.error('Error polling progress:', err);
      
      // Check if it's a 404 error (job not found)
      if (err.response?.status === 404) {
        console.log(`[LegacyImportModal-${instanceId.current}] Job ${jobId} not found (404), stopping polling`);
        // Stop polling for this job
        isPollingRef.current = false;
        currentJobIdRef.current = null;
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setError('Import job not found. It may have been deleted or completed.');
        setIsImporting(false);
      } else if (isPollingRef.current && jobId === currentJobIdRef.current) {
        // Other error but still polling for current job - continue but show error
        console.log(`[LegacyImportModal-${instanceId.current}] Error polling job ${jobId}, will retry:`, err);
        setError('Failed to get import progress. Will retry...');
      }
    }
  }, [files, onImportComplete]);

  // Start import
  const handleStartImport = async () => {
    if (!files || files.length === 0) {
      setError('No files selected');
      return;
    }

    console.log(`[LegacyImportModal-${instanceId.current}] Starting import for ${files.length} file(s)`);
    
    // Clear any existing interval before starting new one
    if (pollIntervalRef.current) {
      console.log(`[LegacyImportModal-${instanceId.current}] Clearing existing interval before new import`);
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    isPollingRef.current = false;
    currentJobIdRef.current = null;

    setIsImporting(true);
    setError(null);
    setProgress(null);
    setImportResults([]);
    
    // File progress tracking has been removed - using overall progress instead

    try {
      console.log(`[LegacyImportModal-${instanceId.current}] Starting import for ${files.length} file(s)`);
      
      // Send all files to the backend at once
      const response = await startLegacyImport(files, {
        skipDuplicates: true,
        batchSize: 100
      });

      console.log(`[LegacyImportModal-${instanceId.current}] Import started with job ID: ${response.jobId}`);
      console.log(`[LegacyImportModal-${instanceId.current}] Files uploaded: ${response.fileCount || files.length}`);
      
      currentJobIdRef.current = response.jobId;
      isPollingRef.current = true;

      // Do initial poll immediately
      pollProgress(response.jobId);

      // Start polling for progress
      const capturedJobId = response.jobId;
      pollIntervalRef.current = setInterval(() => {
        if (isPollingRef.current && capturedJobId === currentJobIdRef.current && isVisibleRef.current) {
          pollProgress(capturedJobId);
        } else {
          // Safety check - clear interval if polling was stopped or job changed
          console.log(`[LegacyImportModal-${instanceId.current}] Interval safety check - stopping polling. isPolling: ${isPollingRef.current}, jobMatch: ${capturedJobId === currentJobIdRef.current}, visible: ${isVisibleRef.current}`);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }, 1000); // Poll every second
    } catch (err: any) {
      console.error('Error starting import:', err);
      const errorMessage = err.response?.data?.message || 'Failed to start import';
      const filesInfo = files.length > 1 ? ` for ${files.length} files` : '';
      setError(`${errorMessage}${filesInfo}`);
      setIsImporting(false);
      isPollingRef.current = false;
      currentJobIdRef.current = null;
    }
  };

  // Calculate progress percentage
  const progressPercentage = progress
    ? Math.round((progress.processedItems / progress.totalItems) * 100)
    : 0;

  // Count total errors and warnings
  const totalErrors = importResults.reduce((sum, result) => sum + result.errors.length, 0);
  const totalWarnings = importResults.reduce((sum, result) => sum + result.warnings.length, 0);

  // Always render the modal structure, control visibility with CSS
  if (!isOpen) {
    return <div style={{ display: 'none' }} />;
  }

  return (
    <div className="legacy-import-modal-overlay">
      <div className="legacy-import-modal">
        <div className="modal-header">
          <h3>Import Legacy Data</h3>
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={isImporting}
            title={isImporting ? "Cannot close during import" : "Close"}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {!isImporting && !progress && (
            <div className="import-start-section">
              <div className="file-info">
                <h4>Selected Files ({files.length})</h4>
                {files.length === 0 ? (
                  <p className="file-name">No files selected</p>
                ) : (
                  <div className="files-list">
                    {files.map((file, index) => (
                      <div key={index} className="file-item">
                        <p className="file-name">{file.name}</p>
                        <p className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    ))}
                    <p className="total-size">
                      Total size: {(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>

              <div className="import-instructions">
                <p>Click the button below to start importing legacy data from the selected XML {files.length > 1 ? 'files' : 'file'}.</p>
                <p>This process may take several minutes depending on the {files.length > 1 ? 'total file size' : 'file size'}.</p>
                {files.length > 1 && (
                  <p className="multiple-files-note">
                    All {files.length} files will be processed together in a single import job.
                  </p>
                )}
                {files.length > 100 && (
                  <div className="warning-message" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '4px' }}>
                    <strong>Warning:</strong> You've selected {files.length} files. For large imports (1000+ files), 
                    consider using the batch import API or splitting into smaller groups to avoid timeout issues.
                  </div>
                )}
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
            </div>
          )}

          {(isImporting || progress) && (
            <div className="import-progress-section">
              <div className="progress-header">
                <h4>Import Progress</h4>
                <span className={`status-badge status-${progress?.status || 'pending'}`}>
                  {progress?.status || 'Starting...'}
                </span>
              </div>

              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <span className="progress-percentage">{progressPercentage}%</span>
              </div>

              <div className="progress-details">
                <p className="current-file">
                  {progress?.currentStep || 'Initializing...'}
                </p>
                {files.length > 1 && (
                  <p className="processing-info">
                    Processing {files.length} files
                  </p>
                )}
                
                <div className="import-stats">
                  <div className="stat-item">
                    <span className="stat-label">Processed:</span>
                    <span className="stat-value">
                      {progress?.processedItems || 0} / {progress?.totalItems || 0}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Successful:</span>
                    <span className="stat-value success">{progress?.successCount || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Errors:</span>
                    <span className="stat-value error">{progress?.errorCount || 0}</span>
                  </div>
                </div>

                {progress?.estimatedTimeRemaining && (
                  <p className="time-remaining">
                    Estimated time remaining: {Math.ceil(progress.estimatedTimeRemaining / 60)} minutes
                  </p>
                )}
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
            </div>
          )}

          {importResults.length > 0 && (
            <div className="import-results-section">
              <h4>Import Complete</h4>
              
              <div className="results-summary">
                <div className="summary-item">
                  <span className="summary-label">Total Claimants:</span>
                  <span className="summary-value">
                    {importResults.reduce((sum, r) => sum + r.claimantsImported, 0)}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Claims:</span>
                  <span className="summary-value">
                    {importResults.reduce((sum, r) => sum + r.claimsImported, 0)}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total Documents:</span>
                  <span className="summary-value">
                    {importResults.reduce((sum, r) => sum + r.documentsImported, 0)}
                  </span>
                </div>
              </div>

              {totalErrors > 0 && (
                <div className="collapsible-section error-section">
                  <button 
                    className="collapsible-header"
                    onClick={() => setShowErrors(!showErrors)}
                  >
                    <span className="collapsible-icon">{showErrors ? '▼' : '▶'}</span>
                    Errors ({totalErrors})
                  </button>
                  {showErrors && (
                    <div className="collapsible-content">
                      {importResults.map((result, idx) => (
                        result.errors.length > 0 && (
                          <div key={idx} className="file-errors">
                            <h5>{result.filename}</h5>
                            <ul>
                              {result.errors.map((error, errorIdx) => (
                                <li key={errorIdx}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              {totalWarnings > 0 && (
                <div className="collapsible-section warning-section">
                  <button 
                    className="collapsible-header"
                    onClick={() => setShowWarnings(!showWarnings)}
                  >
                    <span className="collapsible-icon">{showWarnings ? '▼' : '▶'}</span>
                    Warnings ({totalWarnings})
                  </button>
                  {showWarnings && (
                    <div className="collapsible-content">
                      {importResults.map((result, idx) => (
                        result.warnings.length > 0 && (
                          <div key={idx} className="file-warnings">
                            <h5>{result.filename}</h5>
                            <ul>
                              {result.warnings.map((warning, warningIdx) => (
                                <li key={warningIdx}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="file-results">
                <h5>File Results ({importResults.length} {importResults.length === 1 ? 'file' : 'files'})</h5>
                {importResults.map((result, idx) => (
                  <div key={idx} className={`file-result ${result.status}`}>
                    <div className="file-result-header">
                      <span className="file-name">{result.filename}</span>
                      <span className={`result-status ${result.status}`}>
                        {result.status === 'success' ? '✓ Success' : 
                         result.status === 'warning' ? '⚠ Warning' : '✗ Error'}
                      </span>
                    </div>
                    <div className="file-result-stats">
                      <span>Claimants: {result.claimantsImported}</span>
                      <span>Claims: {result.claimsImported}</span>
                      <span>Documents: {result.documentsImported}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!isImporting && !progress && (
            <button 
              className="import-button" 
              onClick={handleStartImport}
              disabled={!files || files.length === 0}
            >
              Start Import
            </button>
          )}
          
          {importResults.length > 0 && (
            <button 
              className="close-button-footer" 
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegacyImportModal;